/**
 * Emoji Decoration - CodeMirror 6 Emoji 裝飾擴展
 *
 * 將 Unicode emoji 替換為 Twemoji 圖片顯示
 *
 * @module @linkode/web/editor/cm/emojiDecoration
 */

import { Extension } from '@codemirror/state';
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
} from '@codemirror/view';
import emojiRegex from 'emoji-regex';
import twemoji from '@twemoji/api';

// ============================================================================
// Twemoji Widget
// ============================================================================

/**
 * Twemoji Widget - 將 emoji 渲染為 Twemoji 圖片
 */
class TwemojiWidget extends WidgetType {
  constructor(readonly emoji: string) {
    super();
  }

  eq(other: TwemojiWidget): boolean {
    return other.emoji === this.emoji;
  }

  toDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = 'cm-twemoji';
    span.setAttribute('data-emoji', this.emoji);

    // 使用 twemoji.parse 將 emoji 轉換為 img
    span.innerHTML = twemoji.parse(this.emoji, {
      base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji/assets/',
      folder: '72x72',
      ext: '.png',
    });

    return span;
  }

  ignoreEvent(): boolean {
    return false;
  }
}

// ============================================================================
// Decoration Builder
// ============================================================================

/**
 * 建立 emoji decorations
 */
function buildDecorations(view: EditorView): DecorationSet {
  const decorations: { from: number; to: number; decoration: Decoration }[] = [];
  const regex = emojiRegex();

  for (const { from, to } of view.visibleRanges) {
    const text = view.state.sliceDoc(from, to);
    let match;

    while ((match = regex.exec(text)) !== null) {
      const emoji = match[0];
      const start = from + match.index;
      const end = start + emoji.length;

      decorations.push({
        from: start,
        to: end,
        decoration: Decoration.replace({
          widget: new TwemojiWidget(emoji),
        }),
      });
    }
  }

  // 按位置排序
  decorations.sort((a, b) => a.from - b.from);

  return Decoration.set(decorations.map((d) => d.decoration.range(d.from, d.to)));
}

// ============================================================================
// View Plugin
// ============================================================================

/**
 * Emoji Decoration Plugin
 */
const emojiDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = buildDecorations(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildDecorations(update.view);
      }
    }
  },
  {
    decorations: (v) => v.decorations,
  }
);

// ============================================================================
// Styles
// ============================================================================

/**
 * Twemoji 樣式
 */
const emojiStyles = EditorView.baseTheme({
  '.cm-twemoji': {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  '.cm-twemoji img': {
    width: '1.2em',
    height: '1.2em',
    verticalAlign: '-0.2em',
    display: 'inline-block',
    margin: '0 0.05em',
  },
});

// ============================================================================
// Extension Factory
// ============================================================================

/**
 * 建立 Emoji Decoration 擴展
 *
 * @returns CodeMirror 擴展
 *
 * @example
 * ```typescript
 * const extensions = [
 *   ...createMarkdownExtensions(),
 *   createEmojiDecoration(),
 * ];
 * ```
 */
export function createEmojiDecoration(): Extension {
  return [emojiDecorationPlugin, emojiStyles];
}

export default createEmojiDecoration;
